/**
 * MERGE BRAND DOCS (Admin Tool)
 *
 * Companion to MergeBrands.tsx. That tool matches by brand display name
 * and assumes brand doc IDs equal slugs — it fails on brands with auto-
 * generated doc IDs (claimed brands often have these) and on cases where
 * two brand docs have IDENTICAL names but different doc IDs (e.g., the
 * "PopulateBrands generated a duplicate" pattern).
 *
 * This tool works by DOC ID directly. You provide:
 *   - FROM doc ID (the one being deleted)
 *   - TO doc ID (the canonical one being kept)
 *
 * It then:
 *   1. Renames every user item whose `brand` matches FROM.name to TO.name
 *      (with brandLower also updated).
 *   2. Migrates FROM's subcollections (followers, products, collections,
 *      items) into TO's subcollections via setDoc + merge.
 *   3. Updates `events` docs with brandId == FROM.id to brandId = TO.id,
 *      brandName = TO.name.
 *   4. Deletes the FROM brand doc and its subcollections.
 *
 * REQUIRES: isAdmin: true on the user document.
 * Route: /admin/merge-brand-docs
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  collection,
  collectionGroup,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  setDoc,
  deleteDoc,
  updateDoc,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { ArrowLeft } from 'lucide-react';
import { slugify } from '../utils/polishSlug';

interface BrandInfo {
  id: string;
  name: string;
  slug: string | null;
  status: string | null;
  curationStatus: string | null;
  // Counts from LIVE queries — what's actually in the DB right now.
  itemCount: number;
  followerCount: number;
  productCount: number;
  collectionCount: number;
  brandItemCount: number;
  eventCount: number;
  // Snapshot of the brand doc's `stats` field if present. These are
  // maintained by the catalog triggers (or by the legacy MergeBrands tool)
  // and can drift when items get renamed without a stats refresh.
  // Useful to compare against the live counts — if `statsTotalItems = 10`
  // but `itemCount = 0`, the trigger thinks 10 items exist but they're not
  // showing up under this brand's name (case mismatch or rename without
  // recompute).
  statsTotalItems: number | null;
  statsTotalCollectors: number | null;
  statsTotalWishlisted: number | null;
  statsCollectionsCount: number | null;
  polishCount: number | null;
  collectorCount: number | null;
}

interface BrandCandidate {
  id: string;
  name: string;
  slug: string;
}

/**
 * Build the obvious case variants of a brand display name so we can
 * union queries by `brand` (which has a Firestore index) and catch
 * "Cadillacquer", "cadillacquer", "CADILLACQUER", "cadiLLacquer"...
 * Dedupe so we don't run the same query twice.
 */
function buildCaseVariants(name: string): string[] {
  const seen = new Set<string>();
  const push = (s: string) => {
    if (s) seen.add(s);
  };
  push(name);
  push(name.trim());
  push(name.toLowerCase());
  push(name.toUpperCase());
  // Title-case the first letter of each word — common when items were
  // typed manually.
  push(
    name
      .toLowerCase()
      .replace(/\b([a-z])/g, (m) => m.toUpperCase())
  );
  return Array.from(seen);
}

export default function MergeBrandDocs() {
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [fromInfo, setFromInfo] = useState<BrandInfo | null>(null);
  const [toInfo, setToInfo] = useState<BrandInfo | null>(null);
  // Candidates surface when a typed input doesn't resolve to exactly one
  // brand (e.g., typed "cadilacquer" but the doc id is "cadilacquer-1",
  // or there are multiple brands sharing that substring). The user picks
  // from the list which fills the input with the actual doc id.
  const [fromCandidates, setFromCandidates] = useState<BrandCandidate[]>([]);
  const [toCandidates, setToCandidates] = useState<BrandCandidate[]>([]);
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState<string[]>([]);
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user?.uid || null);
      if (user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        setIsAdmin(userDoc.exists() && userDoc.data()?.isAdmin === true);
      } else {
        setIsAdmin(false);
      }
      setCheckingAdmin(false);
    });
    return unsubscribe;
  }, []);

  const addLog = (msg: string) => {
    setResults((prev) => [...prev, msg]);
    console.log(msg);
  };

  /**
   * Try to resolve a user-typed string to a brand doc. Strategies in order:
   *   1. Exact doc id as typed.
   *   2. Exact doc id, lowercased.
   *   3. Exact doc id, slugified (handles spaces / accents).
   *   4. where('slug', '==', input) — direct slug field match.
   *   5. where('slug', '==', slugified input).
   *   6. where('name', '==', input) — case-sensitive name match.
   *
   * Avoids any unfiltered collection list, because some rule sets allow
   * filtered queries (e.g., what BrandPage already does) but block broad
   * list reads. Each step is a single targeted operation.
   *
   * Returns the deduped candidates so the caller can either auto-pick (if
   * exactly one) or show a "did you mean" picker (if many).
   */
  async function resolveBrandInput(input: string): Promise<BrandCandidate[]> {
    const trimmed = input.trim();
    if (!trimmed) return [];

    const seen = new Map<string, BrandCandidate>();
    const addDoc = (
      id: string,
      data: { name?: string; slug?: string } | undefined
    ) => {
      if (seen.has(id)) return;
      seen.set(id, {
        id,
        name: (data?.name as string) || '',
        slug: (data?.slug as string) || '',
      });
    };

    // NOTE: we do NOT bail out after the first hit anymore. Multiple brand
    // docs can share a slug (a common Angry-Harpy / claimed-vs-auto pattern:
    // one auto-id doc with the claimed content + a slug-id doc generated
    // later by PopulateBrands). Running every strategy and letting the
    // candidate picker show ALL unique results is the only way to surface
    // both for the admin to pick FROM and TO.
    const idsToTry = [
      trimmed,
      trimmed.toLowerCase(),
      slugify(trimmed),
    ];
    const seenIds = new Set<string>();
    for (const candidateId of idsToTry) {
      if (!candidateId || seenIds.has(candidateId)) continue;
      seenIds.add(candidateId);
      try {
        const snap = await getDoc(doc(db, 'brands', candidateId));
        if (snap.exists()) addDoc(snap.id, snap.data() as any);
      } catch {
        // continue
      }
    }

    // Filtered queries that mirror the where('slug', '==', X) shape that
    // BrandPage already uses successfully. These are how we surface the
    // auto-id doc whose ID doesn't match the slug.
    const slugsToTry = [trimmed, trimmed.toLowerCase(), slugify(trimmed)];
    const seenSlugs = new Set<string>();
    for (const s of slugsToTry) {
      if (!s || seenSlugs.has(s)) continue;
      seenSlugs.add(s);
      try {
        const qSnap = await getDocs(
          query(collection(db, 'brands'), where('slug', '==', s))
        );
        qSnap.forEach((d) => addDoc(d.id, d.data() as any));
      } catch {
        // continue
      }
    }

    // Case-sensitive name match.
    try {
      const qSnap = await getDocs(
        query(collection(db, 'brands'), where('name', '==', trimmed))
      );
      qSnap.forEach((d) => addDoc(d.id, d.data() as any));
    } catch {
      // continue
    }

    return Array.from(seen.values());
  }

  /**
   * Load a brand doc + count its derived data so the admin can see the
   * blast radius before merging. Each count is wrapped individually so
   * one failing query (commonly the events one, which can trip stricter
   * rules when isAdmin() isn't resolving) doesn't kill the whole preview.
   * Failed counts come back as -1 so the UI can distinguish "0 found"
   * from "couldn't check."
   *
   * Returns null if the brand doc itself doesn't exist.
   */
  async function loadBrandInfo(brandId: string): Promise<BrandInfo | null> {
    const brandDoc = await getDoc(doc(db, 'brands', brandId));
    if (!brandDoc.exists()) return null;
    const data = brandDoc.data();
    const name = (data.name as string) || '';

    const tryCount = async (
      fn: () => Promise<number>,
      label: string
    ): Promise<number> => {
      try {
        return await fn();
      } catch (err: any) {
        console.warn(`[MergeBrandDocs] ${label} count failed for ${brandId}:`, err?.message ?? err);
        return -1;
      }
    };

    // Count items by querying for case variants of the brand name.
    // We deliberately avoid `where('brandLower', '==', ...)` because that
    // would need a separate collectionGroup index that this project hasn't
    // created — querying by `brand` uses the existing index that the
    // legacy MergeBrands tool also relies on. We try the obvious case
    // variants and union the results so we don't undercount.
    const itemCount = await tryCount(async () => {
      const variants = buildCaseVariants(name);
      const seen = new Set<string>();
      for (const variant of variants) {
        try {
          const snap = await getDocs(
            query(collectionGroup(db, 'items'), where('brand', '==', variant))
          );
          for (const d of snap.docs) {
            if (d.ref.parent.parent?.parent.id === 'users') seen.add(d.ref.path);
          }
        } catch {
          // continue to next variant
        }
      }
      return seen.size;
    }, 'items');

    const followerCount = await tryCount(
      async () => (await getDocs(collection(db, 'brands', brandId, 'followers'))).size,
      'followers'
    );
    const productCount = await tryCount(
      async () => (await getDocs(collection(db, 'brands', brandId, 'products'))).size,
      'products'
    );
    const collectionCount = await tryCount(
      async () => (await getDocs(collection(db, 'brands', brandId, 'collections'))).size,
      'collections'
    );
    const brandItemCount = await tryCount(
      async () => (await getDocs(collection(db, 'brands', brandId, 'items'))).size,
      'official items'
    );
    const eventCount = await tryCount(
      async () =>
        (await getDocs(query(collection(db, 'events'), where('brandId', '==', brandId)))).size,
      'events'
    );

    // Extract the brand doc's own stats fields so we can show them
    // alongside live counts.
    const stats = (data.stats as Record<string, any>) || {};
    const numOrNull = (v: any): number | null =>
      typeof v === 'number' ? v : null;

    return {
      id: brandId,
      name,
      slug: (data.slug as string) || null,
      status: (data.status as string) || null,
      curationStatus: (data.curationStatus as string) || null,
      itemCount,
      followerCount,
      productCount,
      collectionCount,
      brandItemCount,
      eventCount,
      statsTotalItems: numOrNull(stats.totalItems),
      statsTotalCollectors: numOrNull(stats.totalCollectors),
      statsTotalWishlisted: numOrNull(stats.totalWishlisted),
      statsCollectionsCount: numOrNull(stats.collectionsCount),
      polishCount: numOrNull(data.polishCount),
      collectorCount: numOrNull(data.collectorCount),
    };
  }

  const handlePreview = async () => {
    if (!fromId.trim() || !toId.trim()) return;
    setLoadingInfo(true);
    setResults([]);
    setFromInfo(null);
    setToInfo(null);
    setFromCandidates([]);
    setToCandidates([]);
    try {
      // Resolve each input. If a side has exactly one match, auto-fill the
      // canonical doc id (so subsequent merge writes are unambiguous) and
      // load the full info. If a side has zero or multiple matches, show
      // the candidates list so the admin can pick.
      const [fromMatches, toMatches] = await Promise.all([
        resolveBrandInput(fromId),
        resolveBrandInput(toId),
      ]);

      let fInfo: BrandInfo | null = null;
      let tInfo: BrandInfo | null = null;

      if (fromMatches.length === 1) {
        if (fromMatches[0].id !== fromId.trim()) setFromId(fromMatches[0].id);
        fInfo = await loadBrandInfo(fromMatches[0].id);
      } else {
        setFromCandidates(fromMatches);
        if (fromMatches.length === 0) addLog(`❌ FROM "${fromId}" — no matches found`);
        else addLog(`⚠️ FROM "${fromId}" — ${fromMatches.length} candidates, pick one below`);
      }

      if (toMatches.length === 1) {
        if (toMatches[0].id !== toId.trim()) setToId(toMatches[0].id);
        tInfo = await loadBrandInfo(toMatches[0].id);
      } else {
        setToCandidates(toMatches);
        if (toMatches.length === 0) addLog(`❌ TO "${toId}" — no matches found`);
        else addLog(`⚠️ TO "${toId}" — ${toMatches.length} candidates, pick one below`);
      }

      setFromInfo(fInfo);
      setToInfo(tInfo);
      if (fInfo && tInfo) {
        addLog(`Both brands resolved. Review counts below before applying.`);
      }
    } catch (err: any) {
      addLog(`💥 Preview failed: ${err?.message ?? err}`);
    } finally {
      setLoadingInfo(false);
    }
  };

  const handleMerge = async () => {
    if (!fromInfo || !toInfo) {
      addLog('⚠️ Click Preview first to load both brands.');
      return;
    }
    if (fromInfo.id === toInfo.id) {
      addLog('⚠️ FROM and TO are the same doc — nothing to do.');
      return;
    }
    if (!confirm(
      `Merge "${fromInfo.name}" (${fromInfo.id}) INTO "${toInfo.name}" (${toInfo.id})?\n\n` +
        `${fromInfo.itemCount} item brand fields will be renamed.\n` +
        `${fromInfo.followerCount + fromInfo.productCount + fromInfo.collectionCount + fromInfo.brandItemCount} subcollection docs will be migrated.\n` +
        `${fromInfo.eventCount} events will be re-pointed.\n` +
        `The FROM brand doc will then be deleted.\n\n` +
        `This cannot be undone.`
    )) return;

    setIsRunning(true);
    addLog(`🔄 Merging "${fromInfo.name}" → "${toInfo.name}"`);

    try {
      // STEP 1: Rename user items whose brand matches FROM. We can't use a
      // single `brandLower` query because there's no collectionGroup index
      // for it — so we instead union queries over each case variant of
      // FROM.name. Both `brand` and `brandLower` get rewritten to TO so
      // downstream (filter UIs that DO use brandLower) work correctly.
      addLog(`📦 Renaming items...`);
      const variants = buildCaseVariants(fromInfo.name);
      addLog(`  searching items with brand in: ${variants.map((v) => `"${v}"`).join(', ')}`);
      const itemsByPath = new Map<string, any>();
      for (const variant of variants) {
        const snap = await getDocs(
          query(collectionGroup(db, 'items'), where('brand', '==', variant))
        );
        for (const d of snap.docs) {
          if (d.ref.parent.parent?.parent.id !== 'users') continue;
          if (!itemsByPath.has(d.ref.path)) itemsByPath.set(d.ref.path, d);
        }
      }
      const itemsSnap = { docs: Array.from(itemsByPath.values()) };
      let userItemsRenamed = 0;
      let itemBatch = writeBatch(db);
      let itemBatchOps = 0;
      for (const d of itemsSnap.docs) {
        if (d.ref.parent.parent?.parent.id !== 'users') continue;
        itemBatch.update(d.ref, {
          brand: toInfo.name,
          brandLower: toInfo.name.toLowerCase(),
        });
        itemBatchOps++;
        userItemsRenamed++;
        if (itemBatchOps >= 400) {
          await itemBatch.commit();
          itemBatch = writeBatch(db);
          itemBatchOps = 0;
        }
      }
      if (itemBatchOps > 0) await itemBatch.commit();
      addLog(`  ✅ Renamed ${userItemsRenamed} user items`);

      // STEP 2: Migrate each subcollection from FROM → TO via setDoc+merge.
      // Each one wrapped individually — `products` in particular has no
      // firestore.rules entry so its read is denied; we log and continue
      // rather than crashing the whole merge.
      const subs = ['followers', 'products', 'collections', 'items'] as const;
      for (const sub of subs) {
        addLog(`📦 Migrating ${sub}...`);
        try {
          const subSnap = await getDocs(collection(db, 'brands', fromInfo.id, sub));
          let migrated = 0;
          for (const d of subSnap.docs) {
            await setDoc(doc(db, 'brands', toInfo.id, sub, d.id), d.data(), { merge: true });
            await deleteDoc(d.ref);
            migrated++;
          }
          addLog(`  ✅ Migrated ${migrated} ${sub}`);
        } catch (err: any) {
          addLog(`  ⚠️ Skipped ${sub} (${err?.message ?? err})`);
        }
      }

      // STEP 3: Re-point events. Skipped (and logged) on permission errors.
      if (fromInfo.eventCount > 0) {
        addLog(`📦 Re-pointing events...`);
        try {
          const eventsSnap = await getDocs(
            query(collection(db, 'events'), where('brandId', '==', fromInfo.id))
          );
          let eventsBatch = writeBatch(db);
          let eventsBatchOps = 0;
          let eventsUpdated = 0;
          for (const e of eventsSnap.docs) {
            eventsBatch.update(e.ref, {
              brandId: toInfo.id,
              brandName: toInfo.name,
            });
            eventsBatchOps++;
            eventsUpdated++;
            if (eventsBatchOps >= 400) {
              await eventsBatch.commit();
              eventsBatch = writeBatch(db);
              eventsBatchOps = 0;
            }
          }
          if (eventsBatchOps > 0) await eventsBatch.commit();
          addLog(`  ✅ Updated ${eventsUpdated} events`);
        } catch (err: any) {
          addLog(`  ⚠️ Skipped events (${err?.message ?? err})`);
        }
      }

      // STEP 4: Recompute TO's stats so the brand card shows the right
      // numbers immediately instead of waiting for a periodic trigger. We
      // recount by the same case-variant union we used to find items in
      // the first place, dedupe by user uid to get collector count.
      addLog(`📊 Recomputing TO brand stats...`);
      try {
        const toVariants = buildCaseVariants(toInfo.name);
        const itemPaths = new Set<string>();
        const userIds = new Set<string>();
        for (const v of toVariants) {
          const snap = await getDocs(
            query(collectionGroup(db, 'items'), where('brand', '==', v))
          );
          for (const d of snap.docs) {
            if (d.ref.parent.parent?.parent.id !== 'users') continue;
            if (itemPaths.has(d.ref.path)) continue;
            itemPaths.add(d.ref.path);
            const uid = d.ref.parent.parent?.id;
            if (uid) userIds.add(uid);
          }
        }
        await updateDoc(doc(db, 'brands', toInfo.id), {
          'stats.totalItems': itemPaths.size,
          'stats.totalCollectors': userIds.size,
          'stats.lastUpdated': serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        addLog(`  ✅ stats.totalItems = ${itemPaths.size}, stats.totalCollectors = ${userIds.size}`);
      } catch (err: any) {
        addLog(`  ⚠️ Stats recompute skipped (${err?.message ?? err})`);
        // Still update updatedAt so the brand doc has a fresh modified
        // timestamp even if stats recompute failed.
        await updateDoc(doc(db, 'brands', toInfo.id), { updatedAt: serverTimestamp() });
      }

      // STEP 5: Delete FROM brand doc.
      await deleteDoc(doc(db, 'brands', fromInfo.id));
      addLog(`🗑️  Deleted FROM brand doc (${fromInfo.id})`);

      addLog(``);
      addLog(`✨ Merge complete!`);
      addLog(`Visit /brands/${toInfo.slug ?? toInfo.id} to verify.`);

      // Reload TO's counts so the admin can see the new totals.
      const refreshed = await loadBrandInfo(toInfo.id);
      setToInfo(refreshed);
      setFromInfo(null);
    } catch (err: any) {
      addLog(`💥 Merge failed: ${err?.message ?? err}`);
      console.error(err);
    } finally {
      setIsRunning(false);
    }
  };

  if (checkingAdmin) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <div className="glass-card p-6 text-center">
          <p className="text-glass-secondary">Checking admin status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-8">
      <Link
        to="/admin"
        className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 mb-4 font-body"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Admin Dashboard
      </Link>

      <div className="glass-card p-6">
        <h1 className="text-2xl font-bold text-glass mb-2 font-heading">
          🔀 Merge Brand Docs by ID (Admin)
        </h1>
        <p className="text-glass-secondary mb-4 font-body">
          For duplicate brand docs that can't be merged via name (e.g., two
          docs with the same name, or auto-generated IDs that don't match
          their slugs). Provide FROM and TO doc IDs from Firebase Console.
          FROM gets fully migrated into TO, then deleted.
        </p>

        {!currentUser ? (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
            <p className="text-red-300">⚠️ You must be logged in to use this tool!</p>
          </div>
        ) : !isAdmin ? (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
            <p className="text-red-300">⚠️ You must be an admin to use this tool!</p>
          </div>
        ) : (
          <div className="mb-6 p-4 bg-green-500/20 border border-green-500/50 rounded-lg">
            <p className="text-green-300">✅ Admin access confirmed</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-glass-secondary mb-1 font-body">
              FROM brand (doc ID or name)
            </label>
            <input
              type="text"
              value={fromId}
              onChange={(e) => {
                setFromId(e.target.value);
                setFromCandidates([]);
                setFromInfo(null);
              }}
              disabled={isRunning}
              className="input-glass font-mono text-sm"
              placeholder="Cadilacquer (name) or doc id"
            />
            {fromCandidates.length > 0 && (
              <div className="mt-2 space-y-1">
                <p className="text-xs text-glass-muted font-body">
                  {fromCandidates.length === 0 ? 'No matches' : 'Pick one:'}
                </p>
                {fromCandidates.map((c) => (
                  <button
                    key={c.id}
                    onClick={async () => {
                      // Load info DIRECTLY here instead of just filling the
                      // field — otherwise re-clicking Preview would re-resolve
                      // and find the same multiple candidates (when two docs
                      // share a slug, as in the Angry Harpy / claimed brand
                      // pattern), trapping the admin in a pick-loop.
                      setFromId(c.id);
                      setFromCandidates([]);
                      setLoadingInfo(true);
                      try {
                        const info = await loadBrandInfo(c.id);
                        setFromInfo(info);
                        addLog(`✅ Selected FROM: ${c.name} (${c.id})`);
                      } catch (err: any) {
                        addLog(`💥 Failed to load ${c.id}: ${err?.message ?? err}`);
                      } finally {
                        setLoadingInfo(false);
                      }
                    }}
                    className="w-full text-left text-xs bg-slate-800 hover:bg-slate-700 px-3 py-2 rounded border border-slate-600 font-body"
                  >
                    <span className="text-glass">{c.name || '(no name)'}</span>
                    <span className="text-glass-muted font-mono"> · {c.id}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-glass-secondary mb-1 font-body">
              TO brand (doc ID or name)
            </label>
            <input
              type="text"
              value={toId}
              onChange={(e) => {
                setToId(e.target.value);
                setToCandidates([]);
                setToInfo(null);
              }}
              disabled={isRunning}
              className="input-glass font-mono text-sm"
              placeholder="Cadillacquer (name) or doc id"
            />
            {toCandidates.length > 0 && (
              <div className="mt-2 space-y-1">
                <p className="text-xs text-glass-muted font-body">
                  {toCandidates.length === 0 ? 'No matches' : 'Pick one:'}
                </p>
                {toCandidates.map((c) => (
                  <button
                    key={c.id}
                    onClick={async () => {
                      // Same pattern as FROM picker — load info inline so
                      // re-clicking Preview doesn't trap us in a pick-loop.
                      setToId(c.id);
                      setToCandidates([]);
                      setLoadingInfo(true);
                      try {
                        const info = await loadBrandInfo(c.id);
                        setToInfo(info);
                        addLog(`✅ Selected TO: ${c.name} (${c.id})`);
                      } catch (err: any) {
                        addLog(`💥 Failed to load ${c.id}: ${err?.message ?? err}`);
                      } finally {
                        setLoadingInfo(false);
                      }
                    }}
                    className="w-full text-left text-xs bg-slate-800 hover:bg-slate-700 px-3 py-2 rounded border border-slate-600 font-body"
                  >
                    <span className="text-glass">{c.name || '(no name)'}</span>
                    <span className="text-glass-muted font-mono"> · {c.id}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mb-6 flex gap-2">
          <button
            onClick={handlePreview}
            disabled={loadingInfo || isRunning || !isAdmin || !fromId.trim() || !toId.trim()}
            className={`px-5 py-2.5 rounded-lg font-medium text-white transition-colors font-body ${
              loadingInfo || isRunning || !isAdmin || !fromId.trim() || !toId.trim()
                ? 'bg-slate-600 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {loadingInfo ? 'Loading…' : '🔎 Preview'}
          </button>
          <button
            onClick={handleMerge}
            disabled={!fromInfo || !toInfo || isRunning || !isAdmin}
            className={`px-5 py-2.5 rounded-lg font-medium text-white transition-colors font-body ${
              !fromInfo || !toInfo || isRunning || !isAdmin
                ? 'bg-slate-600 cursor-not-allowed'
                : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {isRunning ? 'Merging…' : '🔀 Merge & delete FROM'}
          </button>
        </div>

        {/* Projected after-merge state. Shown only when both brands are
            loaded so the admin can see the result before committing. Sums
            are conservative (over-counts when followers overlap because
            setDoc+merge dedupes by uid — actual final number may be lower
            but never higher). Unknown counts (-1) propagate as unknown. */}
        {fromInfo && toInfo && (
          <div className="mb-6 p-4 rounded-lg border border-purple-500/40 bg-purple-500/10">
            <p className="text-xs uppercase tracking-wider text-glass-muted mb-2 font-body">
              ✨ Projected after merge — TO brand &quot;{toInfo.name}&quot; will have:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-sm font-body">
              {(() => {
                const projectSum = (a: number, b: number): string =>
                  a < 0 || b < 0 ? '(?)' : String(a + b);
                const projectDelta = (from: number, to: number): string => {
                  if (from < 0 || to < 0) return '';
                  return ` (was ${to}, +${from} from FROM)`;
                };
                return (
                  <>
                    <p>
                      {projectSum(fromInfo.itemCount, toInfo.itemCount)} user items
                      <span className="text-glass-muted text-xs">
                        {projectDelta(fromInfo.itemCount, toInfo.itemCount)}
                      </span>
                    </p>
                    <p>
                      {projectSum(fromInfo.followerCount, toInfo.followerCount)} followers
                      <span className="text-glass-muted text-xs">
                        {projectDelta(fromInfo.followerCount, toInfo.followerCount)}
                      </span>
                    </p>
                    <p>
                      {projectSum(fromInfo.productCount, toInfo.productCount)} products
                      <span className="text-glass-muted text-xs">
                        {projectDelta(fromInfo.productCount, toInfo.productCount)}
                      </span>
                    </p>
                    <p>
                      {projectSum(fromInfo.collectionCount, toInfo.collectionCount)} collections
                      <span className="text-glass-muted text-xs">
                        {projectDelta(fromInfo.collectionCount, toInfo.collectionCount)}
                      </span>
                    </p>
                    <p>
                      {projectSum(fromInfo.brandItemCount, toInfo.brandItemCount)} official items
                      <span className="text-glass-muted text-xs">
                        {projectDelta(fromInfo.brandItemCount, toInfo.brandItemCount)}
                      </span>
                    </p>
                    <p>
                      {projectSum(fromInfo.eventCount, toInfo.eventCount)} events
                      <span className="text-glass-muted text-xs">
                        {projectDelta(fromInfo.eventCount, toInfo.eventCount)}
                      </span>
                    </p>
                  </>
                );
              })()}
            </div>
            <p className="text-xs text-glass-muted mt-2 italic">
              Conservative sums — followers / products that exist in both
              dedupe via setDoc+merge, so the actual final count may be
              slightly lower. `(?)` means at least one side couldn&apos;t
              be counted (usually a rules denial).
            </p>
          </div>
        )}

        {/* Side-by-side preview */}
        {(fromInfo || toInfo) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {(['fromInfo', 'toInfo'] as const).map((key) => {
              const info = key === 'fromInfo' ? fromInfo : toInfo;
              const label = key === 'fromInfo' ? 'FROM (to be deleted)' : 'TO (kept)';
              const tint = key === 'fromInfo' ? 'bg-red-500/10 border-red-500/40' : 'bg-green-500/10 border-green-500/40';
              return (
                <div key={key} className={`p-4 rounded-lg border ${tint}`}>
                  <p className="text-xs uppercase tracking-wider text-glass-muted mb-2 font-body">{label}</p>
                  {info ? (
                    <div className="space-y-1 text-sm font-body">
                      <p><span className="text-glass-muted">Name:</span> <span className="text-glass">{info.name || '(empty)'}</span></p>
                      <p><span className="text-glass-muted">Slug:</span> <span className="font-mono text-glass">{info.slug ?? '(none)'}</span></p>
                      <p><span className="text-glass-muted">ID:</span> <span className="font-mono text-glass text-xs">{info.id}</span></p>
                      <p><span className="text-glass-muted">Status:</span> <span className="text-glass">{info.status ?? '(none)'}</span></p>
                      <p><span className="text-glass-muted">Curation:</span> <span className="text-glass">{info.curationStatus ?? '(default)'}</span></p>
                      <hr className="border-slate-700 my-2" />
                      <p className="text-xs text-glass-muted uppercase tracking-wider">Live counts</p>
                      <p>{info.itemCount < 0 ? '(?)' : info.itemCount} user items <span className="text-glass-muted text-xs">(by brandLower)</span></p>
                      <p>{info.followerCount < 0 ? '(?)' : info.followerCount} followers</p>
                      <p>{info.productCount < 0 ? '(?)' : info.productCount} products</p>
                      <p>{info.collectionCount < 0 ? '(?)' : info.collectionCount} collections</p>
                      <p>{info.brandItemCount < 0 ? '(?)' : info.brandItemCount} official items</p>
                      <p>{info.eventCount < 0 ? '(?)' : info.eventCount} events</p>
                      {(info.statsTotalItems != null ||
                        info.statsTotalCollectors != null ||
                        info.polishCount != null ||
                        info.collectorCount != null) && (
                        <>
                          <hr className="border-slate-700 my-2" />
                          <p className="text-xs text-glass-muted uppercase tracking-wider">Brand-doc stats <span className="lowercase">(may be stale)</span></p>
                          {info.statsTotalItems != null && <p>stats.totalItems: {info.statsTotalItems}</p>}
                          {info.statsTotalCollectors != null && <p>stats.totalCollectors: {info.statsTotalCollectors}</p>}
                          {info.statsTotalWishlisted != null && <p>stats.totalWishlisted: {info.statsTotalWishlisted}</p>}
                          {info.statsCollectionsCount != null && <p>stats.collectionsCount: {info.statsCollectionsCount}</p>}
                          {info.polishCount != null && <p>polishCount: {info.polishCount}</p>}
                          {info.collectorCount != null && <p>collectorCount: {info.collectorCount}</p>}
                        </>
                      )}
                    </div>
                  ) : (
                    <p className="text-glass-muted text-sm">Not loaded — click Preview</p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {results.length > 0 && (
          <div>
            <h2 className="text-lg font-bold text-glass mb-3 font-body">📋 Log</h2>
            <div className="bg-slate-900 text-green-400 p-4 rounded-lg font-mono text-sm max-h-96 overflow-y-auto border border-slate-700">
              {results.map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 p-4 bg-yellow-500/20 border border-yellow-500/50 rounded-lg">
          <p className="text-yellow-300 text-sm font-body">
            ⚠️ <strong className="text-yellow-200">Tip:</strong> Find brand doc IDs
            in Firebase Console → Firestore → brands. The ID is the doc name
            (often the slug, sometimes auto-generated). Use this tool when
            MergeBrands by name doesn't work — typically because one of the
            brands has an auto-generated ID like "Fioe4n0t3MXDcBiZTPKR".
          </p>
        </div>
      </div>
    </div>
  );
}
